import useStore from "../Store/Store";
import { updateRepoDataInCodeCanvasIndexedDB } from "../utils/CodeCanvasIndexedDB";
import { logger } from "../utils/Logger";
import { v4 as uuidv4 } from "uuid";
import { extractTextFromHTML } from "../utils/MiscUtils";
import { IApplyCellsStyle } from "../Components/SourceDoc/TabViews/SimulationsTab/SimulationsTab";
import { checkDiagramNodeIdMatch } from "../Components/SourceDoc/TabViews/SimulationsTab/SimulationUtils";
import { discardRecordedActions, saveRecordedActions } from "./UndoManager";
import {
  sortRepoDataContent,
  backfillRepoDataContent,
} from "../utils/RepoDataAction";
import Fuse from "fuse.js";
import FuseOptions from "../Components/SearchBar/FuseOptions";
import { buildFlexSearchIndex } from "../utils/FlexSearchIndexer";
import { getMapSize } from "../utils/SizeUtils";
import { produce } from "immer";
import { initializeIgnorePatterns } from "../utils/CodeCanvasIgnore";
import FlexSearch from "flexsearch";
import { downloadGitHubArchive } from "../utils/GitHubArchiveDownloader";
import { ArchiveFile } from "../utils/ZipProcessor";

/**
 * handles creating links  based on destination object of
 * merge of data
 *
 * destionation: key of the receiving object to which the
 * metadata of a cell/repoDataObject will be stored after the link
 */
export async function linkFileToNodeHandler(destination: string) {
  const state = useStore.getState();

  // multi guard
  if (state.multiSelect) {
    const dialogMessage = `You cannot link file to multiple cells! 
      Please select a single cell before linking!`;
    state.setDialog("WARNING_DIALOG", {
      message: dialogMessage,
    });
    return;
  }

  // metadata to be copied to repo asset
  else if (destination === state.currentPath) {
    state.setRepoObjectAttribute(
      state.currentPath,
      "wiki",
      state.repoData[state.selectedCell].wiki
    );
  }
  // only assign new name if cellName was previously empty
  else if (
    destination === state.selectedCell &&
    !state.repoData[state.selectedCell].cellName
  ) {
    useStore.getState().postToDrawio({
      action: "NEW_CELL_NAME",
      data: {
        cellId: state.selectedCell,
        newName: state.repoData[state.currentPath].fileName,
      },
    });
  }
  let newRepoObjectAttributes = {
    cellId: state.selectedCell,
    cellName: state.repoData[state.selectedCell].cellName,
  };
  state.setRepoObjectAttributes(state.currentPath, newRepoObjectAttributes);
  // // after a link, the previous cellId linked object will be deleted
  state.deleteRepoObject(state.selectedCell);
  state.setNewCellToPath(state.selectedCell, state.currentPath);
  await updateRepoDataInCodeCanvasIndexedDB();
}

/**
 * handles delete operation from CC
 */
export async function deleteCellsHandler(removedCells: Array<string>) {
  removedCells.forEach((cellId) => {
    const state = useStore.getState();
    let path = cellId;
    if (cellId in state.cellToPath) {
      path = state.cellToPath[cellId];
      state.deleteCellToPath(cellId);
    }
    if (path in state.repoData) {
      state.deleteRepoObjectAttributes(path, ["cellName", "cellId"]);
      if (state.repoData[path]?.parentPath) {
        state.deleteChild(state.repoData[path]?.parentPath, cellId);
      }
      // TODO: Warn before deletion with wiki
      let curRepoDataObject = state.repoData[path];
      if (
        curRepoDataObject.type !== "blob" &&
        curRepoDataObject.type !== "tree"
      ) {
        let destPath = curRepoDataObject.parentPath;
        // TODO: Warn before deletion with wiki
        if (state.deleteMode === "saveWiki") {
          if (destPath && curRepoDataObject?.wiki) {
            let newWikiEntry = `<b>Cell:{L${curRepoDataObject?.startLine}-${curRepoDataObject?.endLine}} wiki:</b>\n${curRepoDataObject?.wiki}`;
            let existingWiki = state.repoData[destPath]?.wiki;
            let mergedWiki = `${
              existingWiki ? existingWiki : "\n"
            }${newWikiEntry}`;
            state.setRepoObjectAttribute(destPath, "wiki", mergedWiki);
          }
        }
        state.deleteRepoObject(path);
      } else {
        if (state.deleteMode === "deleteWiki") {
          state.deleteRepoObjectAttribute(curRepoDataObject.path, "wiki");
        }
      }
    }
  });
  const state = useStore.getState();
  state.setSelectedCell("");
  state.setCurrentPath(state.currentRepo);
  updateRepoDataInCodeCanvasIndexedDB();
}

/**
 * handles add operation from CC
 */
export async function addCellsHandler(newCellIds: Array<any>) {
  const state = useStore.getState();
  newCellIds.forEach((newCell) => {
    let { newCellId, newCellName, parentId, children } = newCell;
    newCellName = extractTextFromHTML(newCellName);

    // skip if cellId already added - protection for duplicate add
    // messages from CC
    if (newCellId in state.cellToPath || newCellId in state.repoData) {
      return;
    }

    // protecting against double message when selected cell
    // is already the current path
    if (state.currentPath !== newCellId) {
      if (
        state.isCellCreationRequestInitiatedFromCodeCanvas &&
        state.currentPath !== state.currentRepo && // not root
        !state.selectedCell &&
        newCellId
      ) {
        let newRepoObjectAttributes = {
          cellId: newCellId,
          cellName: state.repoData[state.currentPath].fileName,
          visible: true,
        };
        state.setRepoObjectAttributes(
          state.currentPath,
          newRepoObjectAttributes
        );
        state.setNewCellToPath(newCellId, state.currentPath);
        state.setIsCellCreationRequestInitiatedFromCodeCanvas(false);
      } else if (
        state.isCellCreationRequestInitiatedFromCodeCanvas &&
        state.repoData[state.selectedCell]?.parentPath === state.currentPath
      ) {
        if (newCellName) {
          let newRepoObjectAttributes = {
            cellId: newCellId,
            cellName: newCellName,
          };
          state.setRepoObjectAttributes(
            state.currentPath,
            newRepoObjectAttributes
          );
        } else {
          state.setRepoObjectAttribute(state.currentPath, "cellId", newCellId);
        }
        state.setNewCellToPath(newCellId, state.currentPath);
        state.setIsCellCreationRequestInitiatedFromCodeCanvas(false);
      }
      // new node with no links to be added to repoData
      else {
        let newRepoObject = {
          path: newCellId,
          cellId: newCellId,
          type: "cell",
          visible: true,
        };
        if (parentId) newRepoObject["parentCellId"] = parentId;
        if (newCellName) newRepoObject["cellName"] = newCellName;
        state.setNewRepoObject(newCellId, newRepoObject);
      }

      children?.forEach((childId) => {
        let newRepoObject = {
          path: childId,
          cellId: childId,
          type: "cell",
          parentCellId: newCellId,
          visible: true,
        };
        state.setNewRepoObject(childId, newRepoObject);
      });
    }
  });
  updateRepoDataInCodeCanvasIndexedDB();
}
/**
 * handles newly selectedCell from DrawIO
 *
 * newCellId: cellId of newly selected Cell
 * multiSelect: boolean to handle case where multiple cells are selected
 */
export function newSelectedCellHandler(newCellIds: Array<string>) {
  useStore.getState().setFlag("isHandlingSelectionChange", true);

  if (useStore.getState().pausePathChangeToDrawio) {
    useStore.getState().setPausePathChangeToDrawio(false);
    discardRecordedActions("*");
  }
  useStore.getState().setLoadingNotification("");

  if (!newCellIds || !newCellIds.length) {
    useStore.getState().setSearchResults(null);
    useStore.getState().setCurrentPath(useStore.getState().currentRepo);
    useStore.getState().setSelectedCell("");
    useStore.getState().setFlag("isHandlingSelectionChange", false);
    return;
  }

  logger.debug.info(`newSelectedCellHandler: ${newCellIds}`);

  let multiSelect = newCellIds?.length > 1;
  let newCellId = "";
  if (!multiSelect) newCellId = newCellIds[0];
  // protection from infinite loops with drawio or if multi select
  if (newCellId !== useStore.getState().selectedCell || multiSelect) {
    useStore.getState().setMultiSelect(multiSelect);

    if (multiSelect) {
      // - setting selectedCell to multi but value not ready anywhere
      // - only setting to this invalid value to avoid any bugs by retaining
      //   the selectedCell even when multiple are selected

      // want to highlight file cell and children cell so need to check if all
      // new cellIds are related to the same file. Then we dont adjust currentPath
      // and keep the file selected which initiated all the cells getting selected
      // in drawio
      let firstParentPath = "";
      if (newCellIds[0] in useStore.getState().cellToPath) {
        firstParentPath = useStore.getState().cellToPath[newCellIds[0]];
      } else {
        firstParentPath =
          useStore.getState().repoData[newCellIds[0]]?.parentPath;
      }
      if (
        firstParentPath &&
        newCellIds.every(
          (id) =>
            useStore.getState().repoData[id]?.parentPath === firstParentPath ||
            useStore.getState().repoData[useStore.getState().cellToPath[id]]
              ?.path === firstParentPath
        )
      ) {
        useStore.getState().setSearchResults(null);
        useStore
          .getState()
          .setSelectedCell(
            useStore.getState().repoData[firstParentPath]?.cellId
          );
        useStore.getState().setCurrentPath(firstParentPath, true);
        useStore.getState().setFlag("isHandlingSelectionChange", false);
        return;
      } else {
        useStore.getState().setSearchResults(null);
        useStore.getState().setSelectedCell("multi");
        useStore.getState().setCurrentPath("multi", true);
      }
    } else {
      useStore.getState().setSelectedCell(newCellId);

      // If we're in code linking mode, handle the link and exit the mode
      if (useStore.getState().pausePathChangeToCodeCanvas) {
        const selectedCell = useStore.getState().selectedCell;
        const repoData = useStore.getState().repoData;
        const cellToPath = useStore.getState().cellToPath;
        const setDialog = useStore.getState().setDialog;

        // Get cell data from either direct reference or linked path
        const cellItemData =
          repoData[selectedCell] || repoData[cellToPath[selectedCell]];
        const currentPath = useStore.getState().currentPath;
        const repoItemData = repoData[currentPath];

        // Check if cell is already linked
        if (
          selectedCell in cellToPath ||
          (repoData[selectedCell]?.cellId && repoData[selectedCell]?.parentPath)
        ) {
          const dialogMessage = `The selected cell: "${
            cellItemData.cellId
          }" is already 
              linked to the file: "${
                cellItemData.fileName || "Untitled Cell"
              }", 
              please unselect the cell or select an un-linked cell to create a link to file: "${
                repoItemData.fileName
              }"`;
          setDialog("WARNING_DIALOG", {
            message: dialogMessage,
          });
        } else {
          // Only proceed with linking if cell is not already linked
          newChildCodeCellHandler(
            useStore.getState().selectionStartLine,
            useStore.getState().selectionEndLine,
            true // connectToExistingCell
          );
        }
        // Exit the linking mode regardless of whether link was created
        useStore.getState().setPausePathChangeToCodeCanvas(false);
      }

      if (!newCellId) {
        useStore
          .getState()
          .setCurrentPath(useStore.getState().currentRepo, false, true);
      }
      // file already linked to file/folder
      if (newCellId in useStore.getState().cellToPath) {
        let path = useStore.getState().cellToPath[newCellId];
        if (useStore.getState().repoData[path].type === "tree") {
          useStore.getState().setShowCellToFolderParentPath(true);
        }
        useStore.getState().setSearchResults(null);
        useStore.getState().setCurrentPath(path, false, true);
      }

      // has exiting data but not linked to file/folder
      else if (newCellId in useStore.getState().repoData) {
        useStore.getState().setSearchResults(null);
        useStore
          .getState()
          .setCurrentPath(
            useStore.getState().repoData[newCellId].path,
            false,
            true
          );
      }
    }
  }
  useStore.getState().setFlag("isHandlingSelectionChange", false);
}

/**
 * handles newly selectedCell from DrawIO
 *
 * @param silentChange - flag to determine whether or not to notify drawio
 * of path change
 *  - used to avoid race conditions and loops during edge condition
 * such as multiple cell addition in a single update
 *
 * @param isDrawioEventOrigin - flag to determine whether or not the event orginated from drawio
 *
 */
export async function currentPathChangeHandler(
  silentChange: boolean,
  isDrawioEventOrigin: boolean = false
) {
  const state = useStore.getState();
  let currentRepoData = state.repoData[state.currentPath];
  let cellId = currentRepoData?.cellId;
  // want to set selectedCell to null/cellId if not linking with
  // the current path change. otherwise, cell will be set to selected
  // after the new cell is added to the graph/linked
  if (!silentChange && !state.multiSelect) {
    state.setSelectedCell(cellId);
  }

  let clearSelection = false;

  // if a file/folder is selected without cell, must
  // send a deselect cells flag to codecanvas
  //
  // if file/folder does have cell, then must highlight
  // as well as select the cell
  if (
    currentRepoData?.type === "blob" ||
    currentRepoData?.type === "tree" ||
    currentRepoData?.parentPath
  ) {
    if (!cellId && !silentChange) {
      clearSelection = true;
    }
  }

  if (
    currentRepoData?.startLine &&
    currentRepoData?.endLine &&
    !state.searchResults
  ) {
    // TODO: talk to Nasser about this
    state.selectedTab === 0 && state.setSelectedTab(1);
  } else if (
    !(currentRepoData?.type === "blob") &&
    !currentRepoData?.startLine &&
    !currentRepoData?.endLine &&
    !currentRepoData?.children &&
    !currentRepoData?.wiki &&
    state.selectedTab !== 3
  ) {
    state.setSelectedTab(0);
  }

  // only send this message to change selections
  // if not linkking the current file/cell
  if (!silentChange) {
    const setFlag = useStore.getState().setFlag;
    setFlag("isChangingPath", true);
    useStore
      .getState()
      .postToDrawioWaitForResponse({
        action: "CURRENT_PATH_CHANGE",
        data: {
          selectId: cellId,
          clearSelection: clearSelection,
          isDrawioEventOrigin: isDrawioEventOrigin,
        },
      })
      .finally(() => setFlag("isChangingPath", false));
  }
}

/**
 * handle label changes
 */
export async function labelChangeHandler(newLabel: string, cellId: string) {
  let destinationPath = useStore.getState().cellToPath[cellId] || cellId;
  useStore
    .getState()
    .setRepoObjectAttribute(
      destinationPath,
      "cellName",
      extractTextFromHTML(newLabel)
    );
  updateRepoDataInCodeCanvasIndexedDB();
}

/**
 * handle label changes
 */
export async function newCellToDrawioHandler(newCellName?: string) {
  const state = useStore.getState();
  await state.postToDrawioWaitForResponse({
    action: "NEW_LINKED_CELL",
    data: { newCellName: newCellName },
  });
  await updateRepoDataInCodeCanvasIndexedDB();
}

export async function newChildCodeCellHandler(
  startLine,
  endLine,
  connectToExistingCell: boolean = false
) {
  const state = useStore.getState();
  let uuidSuffix = uuidv4();
  let childKey = "";

  let parentRepoData = state.repoData[state.currentPath];
  if (parentRepoData.type !== "blob") {
    parentRepoData = state.repoData[parentRepoData.parentPath];
  }
  let parentFileName = parentRepoData?.fileName;
  let newCellName = "";
  if (startLine === endLine) {
    newCellName = `${parentFileName} {L${startLine}}`;
  } else {
    newCellName = `${parentFileName} {L${startLine}:L${endLine}}`;
  }
  let parentCellId = parentRepoData?.cellId;
  let parentPath = parentRepoData?.path;

  if (!connectToExistingCell) {
    childKey = state.currentPath + "-" + uuidSuffix;
    const visible = parentRepoData?.visible === false ? false : true;
    let newRepoObject = {
      cellName: newCellName,
      cellId: childKey,
      fileName: parentFileName,
      parentPath: parentPath,
      parentCellId: parentCellId,
      path: childKey,
      startLine: startLine,
      endLine: endLine,
      visible: visible,
    };

    state.setNewRepoObject(childKey, newRepoObject);
  } else {
    childKey = state.selectedCell;
    let destCell = state.repoData[childKey];
    const visible = destCell?.visible === false ? false : true;
    let codeLinkedCell = {
      ...destCell,
      cellName: destCell.cellName || newCellName,
      fileName: parentFileName,
      parentPath: parentPath,
      parentCellId: parentCellId,
      path: childKey,
      startLine: startLine,
      endLine: endLine,
      visible: visible,
    };

    state.setRepoObjectEntry(childKey, codeLinkedCell);

    if (!destCell.cellName) {
      state.setRepoObjectAttribute(childKey, "cellName", newCellName);
      useStore.getState().postToDrawio({
        action: "NEW_CELL_NAME",
        data: {
          cellId: childKey,
          newName: newCellName,
        },
      });
    }
  }

  if (!parentRepoData?.children) {
    state.setRepoObjectAttribute(parentPath, "children", [childKey]);
  } else {
    state.setRepoObjectAttribute(parentPath, "children", [
      ...parentRepoData?.children,
      childKey,
    ]);
  }

  if (!connectToExistingCell) {
    state.setCurrentPath(childKey);
    state.postToDrawioWaitForResponse({
      action: "NEW_LINKED_CELL",
      data: {
        newCellName: newCellName,
        parentCellId: parentCellId,
        cellId: childKey,
      },
    });
  } else {
    state.setCurrentPath(state.selectedCell);
  }
  state.setPausePathChangeToDrawio(false);
  state.setLoadingNotification("");
  await updateRepoDataInCodeCanvasIndexedDB();
  state.setSelectedText("");
  await saveRecordedActions("ADD_CODE_NODE");
}

// cellIdentifier: cellId || path
export async function unlinkCell(
  cellIdentifier: string,
  wikiDest: string,
  enterEditMode: boolean = false
) {
  let state = useStore.getState();
  if (enterEditMode) {
    state.setPausePathChangeToDrawio(true);
  }
  let pathToUnlink = state.cellToPath[cellIdentifier] || cellIdentifier;
  let cellToUnlink = state.repoData[pathToUnlink];

  if (cellToUnlink.parentPath) {
    state.deleteChild(cellToUnlink.parentPath, cellToUnlink.cellId);
    state.deleteRepoObjectAttributes(pathToUnlink, [
      "parentCellId",
      "fileName",
      "endLine",
      "startLine",
      "parentPath",
    ]);
    if (wikiDest === "file") {
      state.deleteRepoObjectAttribute(pathToUnlink, "wiki");
      let newWikiEntry = `<b>Cell:{L${cellToUnlink?.startLine}-${cellToUnlink?.endLine}} wiki:</b>\n${cellToUnlink?.wiki}`;
      let existingWiki = state.repoData[cellToUnlink.parentPath]?.wiki;
      let mergedWiki = `${existingWiki ? existingWiki : "\n"}${newWikiEntry}`;
      state.setRepoObjectAttribute(cellToUnlink.parentPath, "wiki", mergedWiki);
    }
    state.setCurrentPath(cellToUnlink.parentPath);
    await state.setSelectedTab(1);
  } else {
    state.deleteCellToPath(cellToUnlink.cellId);
    state.deleteRepoObjectAttributes(pathToUnlink, ["cellName", "cellId"]);
    state.setNewRepoObject(cellToUnlink.cellId, {
      path: cellToUnlink.cellId,
      cellId: cellToUnlink.cellId,
      cellName: cellToUnlink.cellName,
      visible: cellToUnlink?.visible === false ? false : true,
    });
    if (wikiDest === "cell") {
      state.deleteRepoObjectAttribute(pathToUnlink, "wiki");
      state.setRepoObjectAttribute(
        cellToUnlink.cellId,
        "wiki",
        cellToUnlink.wiki
      );
    }
    state.setCurrentPath(cellToUnlink.cellId);
  }

  await updateRepoDataInCodeCanvasIndexedDB();
  await saveRecordedActions("UNLINK");
}

export async function toggleSingleCellVisibility(diagramNodeId, visible) {
  var state = useStore.getState();
  var cellToToggle =
    state.repoData[diagramNodeId] ||
    state.repoData[state.cellToPath[diagramNodeId]];
  if (checkDiagramNodeIdMatch(cellToToggle)) {
    return;
  }
  var actionData = {} as IApplyCellsStyle;
  // When the component mounts, set the temporary styles on the diagram node
  if (!cellToToggle || cellToToggle?.visible === false) {
    if (visible) {
      actionData = {
        cellIds: [diagramNodeId],
        // mxGraph styles
        style: {
          visible: true,
        },
        options: {
          temporaryStyleChange: true,
        },
      };

      useStore
        .getState()
        .postToDrawioWaitForResponse({
          action: "CHANGE_CELLS_STYLE",
          data: actionData,
        })
        .catch((error) => {
          logger.error(error);
          logger.error(`Could not apply temporary styles to hidden nodes!}`);
          useStore
            .getState()
            .setErrorNotification(
              `Could not apply temporary styles to hidden nodes!`
            );
        });
    } else {
      if (!useStore.getState()?.simulationsState?.isEditingSimulations) {
        return useStore
          .getState()
          .postToDrawioWaitForResponse({
            action: "CHANGE_CELLS_STYLE",
            data: {
              cellIds: [diagramNodeId],
              style: {
                visible: visible,
              },
              options: {
                temporaryStyleChange: true,
              },
            },
          })
          .catch((error) => {
            logger.error("Error hiding cells: ", error);
          });
      }
    }
  }
}

export async function setCellTags(
  currentRepoData: Object,
  newRepoData: Object
) {
  let cellIdsToTagActions = {};
  // handle special case of entire repo object entry deletion
  Object.entries(currentRepoData).forEach(([key, value]) => {
    if (!newRepoData[key]) {
      cellIdsToTagActions[key] = {
        remove: ["linkedTag", "wikiTag"],
      };
    }
  });

  // post to drawio
  if (Object.keys(cellIdsToTagActions).length) {
    useStore
      .getState()
      .postToDrawioWaitForResponse({
        action: "SET_CELLS_TAGS",
        data: cellIdsToTagActions,
      })
      .then((resp) => {
        if (resp.status === "SUCCESS") return;
      })
      .catch((err) => {
        logger.error.log("Error setting cell tags", err);
      });
  }

  cellIdsToTagActions = {};
  // diff each object as normal
  Object.entries(newRepoData).forEach(([key, value]) => {
    // new repoData entry
    if (!(key in currentRepoData)) {
      let tagsToAdd = [];
      if (value?.cellId) {
        // checking fileName to determine if cell is linked
        if (value?.fileName) {
          tagsToAdd.push("linkedTag");
        }
        if (value?.wiki) {
          tagsToAdd.push("wikiTag");
        }
      }
      // might need to handle children case
      // existing repoData entry
      if (tagsToAdd.length > 0) {
        cellIdsToTagActions[value.cellId] = {
          add: tagsToAdd,
        };
      }
    } else {
      let tagsToAdd = [];
      // link changed
      if (value?.fileName && value?.cellId) {
        tagsToAdd.push("linkedTag");
      } else {
        // Unlinked cell (cellId was removed)
        if (currentRepoData[key].cellId) {
          cellIdsToTagActions[currentRepoData[key].cellId] = {
            remove: ["linkedTag"],
          };
        }
      }

      // wiki changed
      if (value?.wiki && value?.cellId) {
        tagsToAdd.push("wikiTag");
      } else {
        // Unlinked cell (cellId was removed)
        if (currentRepoData[key].cellId) {
          if (
            cellIdsToTagActions[currentRepoData[key].cellId]?.remove?.length > 0
          ) {
            cellIdsToTagActions[currentRepoData[key].cellId].remove.push(
              "wikiTag"
            );
          } else {
            cellIdsToTagActions[currentRepoData[key].cellId] = {
              remove: ["wikiTag"],
            };
          }
        }
      }

      if (value?.cellId && tagsToAdd.length > 0) {
        // if newRepoData cellId is the same as currentRepoData cellId, then we need to add the tags
        if (
          value.cellId === currentRepoData[key].cellId &&
          cellIdsToTagActions[value.cellId]
        ) {
          cellIdsToTagActions[value.cellId].add = tagsToAdd;
        } else {
          cellIdsToTagActions[value.cellId] = {
            add: tagsToAdd,
          };
        }
      }
    }
  });

  if (Object.keys(cellIdsToTagActions).length) {
    useStore
      .getState()
      .postToDrawioWaitForResponse({
        action: "SET_CELLS_TAGS",
        data: cellIdsToTagActions,
      })
      .then((resp) => {
        if (resp.status === "SUCCESS") return;
      })
      .catch((err) => {
        logger.error.log("Error setting cell tags", err);
      });
  }
}
let isIndexingInProgress = false;

export const setRepoData = async (
  set: (fn: (state: any) => any) => void,
  repo: Record<string, any>,
  doLoadDiagram = true
) => {
  // We will load ignore patterns later, after repoData has been stored, so
  // that the helper can inspect the freshly-available repo metadata.
  const repoSize = Object.keys(repo).length;
  const isLargeRepo = repoSize > 1000;

  if (isLargeRepo) {
    console.debug(`[Store] Processing large repository with ${repoSize} files`);
  }

  let sortedContent = sortRepoDataContent(repo);
  let backFilledContent = backfillRepoDataContent(sortedContent);
  useStore.getState().setIsRetrievingRepoData(false);

  // Use a more memory-efficient approach for large repositories
  let myFuse;
  try {
    const fuseData = Object.values(repo).filter(
      (item) => item.path !== useStore.getState().currentRepo
    );

    if (isLargeRepo) {
      // For large repos, limit the data we index for fuzzy search to prevent memory issues
      const limitedData = fuseData.slice(0, 5000); // Limit to first 5000 items
      console.debug(
        `[Store] Limiting fuzzy search data to ${limitedData.length} items for performance`
      );
      myFuse = new Fuse(limitedData, FuseOptions);
    } else {
      myFuse = new Fuse(fuseData, FuseOptions);
    }

    useStore.getState().setFuse(myFuse);
  } catch (error) {
    console.warn("[Store] Failed to create fuzzy search index:", error);
    // Continue without fuzzy search if it fails
  }

  const currentRepoData = useStore.getState().repoData;
  const newRepoData = backFilledContent;

  // For large repositories, skip cell tagging to prevent performance issues
  if (!isLargeRepo) {
    setCellTags(currentRepoData, newRepoData);
  } else {
    console.debug(
      "[Store] Skipping cell tagging for large repository to improve performance"
    );
  }

  set(
    produce((state: any) => {
      state.repoData = backFilledContent;
      state.currentPath = `${state.currentRepo}`;
    })
  );

  // Now that repoData is committed to store, initialise the ignore patterns.
  // This guarantees that `.codecanvasignore` (if present in this repo) can be
  // discovered before any downstream consumers (e.g. FlexSearch indexing)
  // need the data.
  await initializeIgnorePatterns();

  if (doLoadDiagram) {
    useStore.getState().setDoLoadDiagram(true);
  }

  // Only build FlexSearch index for local mode
  if (useStore.getState().session.mode === "local") {
    // Check if a valid index already exists to avoid unnecessary re-indexing
    const currentState = useStore.getState();
    const hasValidIndex =
      currentState.fileIndex &&
      currentState.documentMap &&
      currentState.documentMap.size > 0;

    // Skip re-indexing if we already have a valid index and we're not explicitly
    // requesting a full rebuild (for example, when saving a file)
    if (hasValidIndex && !doLoadDiagram) {
      logger.debug.info(
        "[Store] Valid FlexSearch index already exists, skipping indexing"
      );
      return;
    }

    // Guard against multiple indexing processes
    if (isIndexingInProgress) {
      logger.debug.info(
        "[Store] Indexing already in progress, skipping new request"
      );
      return;
    }

    isIndexingInProgress = true;
    logger.debug.info("[Store] Starting FlexSearch index build");

    let lastProgress = {
      total: 0,
      current: 0,
      isIndexing: true,
    };

    try {
      const result = await buildFlexSearchIndex(
        repo,
        useStore.getState().currentRepo,
        (progress) => {
          // Only update if there's an actual change in progress
          if (
            progress.isIndexing &&
            (progress.current !== lastProgress.current ||
              progress.total !== lastProgress.total)
          ) {
            lastProgress = progress;
            logger.debug.info(
              `[Store] Updating progress: ${progress.current}/${
                progress.total
              } (${Math.round((progress.current / progress.total) * 100)}%)`
            );
            set(
              produce((state: any) => {
                state.indexingProgress = progress;
              })
            );
          }
        }
      );

      logger.debug.info("[Store] Index build complete, updating final state");

      // Batch update all indexing-related state at once
      set(
        produce((state: any) => {
          console.debug("[Store] Cleaning up old data");
          // Clean up old data first
          if (state.fileIndex) {
            state.fileIndex = null;
          }
          if (state.documentMap) {
            state.documentMap.clear();
          }

          console.debug("[Store] Setting new data");
          // Set new data
          state.fileIndex = result.fileIndex;
          state.documentMap = result.documentMap;
          state.indexingProgress = result.indexingProgress;
        })
      );
      logger.debug.info("[Store] Final state update complete");
      const docMap = useStore.getState().documentMap;
      logger.debug.info(`[Store] documentMap entries: ${docMap.size}`);
      try {
        const { kb, tokenEstimate } = getMapSize(docMap);
        logger.debug.info(`[Store] documentMap JSON size: ${kb} KB`);
        logger.debug.info(
          `[Store] documentMap approx tokens: ${tokenEstimate} tokens`
        );
      } catch (err) {
        console.warn("[Store] Unable to calculate documentMap JSON size", err);
      }
    } catch (error) {
      console.error("[Store] Error during indexing or state update:", error);
      // Reset state on error
      set(
        produce((state: any) => {
          state.fileIndex = null;
          if (state.documentMap) {
            state.documentMap.clear();
          }
          state.documentMap = new Map();
          state.indexingProgress = {
            total: 0,
            current: 0,
            isIndexing: false,
          };
        })
      );
      throw error;
    } finally {
      // Always reset the indexing flag
      isIndexingInProgress = false;
    }
  }
  // Add GitHub archive download for GitHub modes
  else if (
    useStore.getState().session.mode === "github" ||
    useStore.getState().session.mode === "unauthenticatedGithub"
  ) {
    const currentState = useStore.getState();
    const { currentRepo, currentBranch, currentRepoMetadata } = currentState;

    // Check if a valid index already exists to avoid unnecessary re-processing
    const hasValidIndex =
      currentState.fileIndex &&
      currentState.documentMap &&
      currentState.documentMap.size > 0;

    // Skip re-processing if we already have a valid index and we're not explicitly
    // requesting a full rebuild
    if (hasValidIndex && !doLoadDiagram) {
      console.debug(
        "[Store] Valid FlexSearch index already exists for GitHub mode, skipping download and indexing"
      );
      return;
    }

    // Guard against multiple download/indexing processes
    if (isIndexingInProgress) {
      console.debug(
        "[Store] Download/indexing already in progress, skipping new request"
      );
      return;
    }

    isIndexingInProgress = true;
    console.debug(
      `[Store] Starting GitHub archive download for ${currentRepoMetadata?.owner}/${currentRepo}@${currentBranch}`
    );

    try {
      // Reset download progress
      set(
        produce((state: any) => {
          state.downloadProgress = {
            phase: "downloading",
            bytesDownloaded: 0,
            totalBytes: 0,
            filesProcessed: 0,
            totalFiles: 0,
            isDownloading: true,
          };
        })
      );

      // Download and extract GitHub archive
      const { repoData: archiveFiles, totalFiles } =
        await downloadGitHubArchive(
          currentRepoMetadata?.owner || "",
          currentRepo,
          currentBranch,
          (progress) => {
            // Update download progress
            set(
              produce((state: any) => {
                state.downloadProgress = {
                  ...state.downloadProgress,
                  phase: progress.phase,
                  bytesDownloaded: progress.bytesDownloaded || 0,
                  totalBytes: progress.totalBytes || 0,
                  filesProcessed: progress.filesProcessed || 0,
                  totalFiles: progress.totalFiles || 0,
                  isDownloading: true,
                };
              })
            );
          }
        );

      console.debug(
        `[Store] Archive download complete, ${totalFiles} files extracted`
      );

      // Merge archive files with existing repo structure more efficiently
      const enrichedRepo = { ...repo };
      const archiveEntries = Object.entries(archiveFiles);
      const isLargeArchive = archiveEntries.length > 1000;

      if (isLargeArchive) {
        console.debug(
          `[Store] Processing large archive with ${archiveEntries.length} files in batches`
        );

        // Process in smaller batches to avoid overwhelming the JavaScript engine
        const BATCH_SIZE = 100;
        for (let i = 0; i < archiveEntries.length; i += BATCH_SIZE) {
          const batch = archiveEntries.slice(i, i + BATCH_SIZE);

          batch.forEach(([path, file]) => {
            // Type assertion to fix TypeScript errors
            const typedFile = file as ArchiveFile;

            if (enrichedRepo[path]) {
              // Add content to existing file entry
              enrichedRepo[path] = {
                ...enrichedRepo[path],
                content: typedFile.content,
              };
            } else {
              // Add new file entry
              enrichedRepo[path] = {
                type: "blob",
                path: typedFile.path,
                fileName: typedFile.fileName,
                content: typedFile.content,
              };
            }
          });

          // Small delay between batches for large archives
          if (i + BATCH_SIZE < archiveEntries.length) {
            await new Promise((resolve) => setTimeout(resolve, 1));
          }
        }
      } else {
        // Process normally for smaller archives
        archiveEntries.forEach(([path, file]) => {
          // Type assertion to fix TypeScript errors
          const typedFile = file as ArchiveFile;

          if (enrichedRepo[path]) {
            // Add content to existing file entry
            enrichedRepo[path] = {
              ...enrichedRepo[path],
              content: typedFile.content,
            };
          } else {
            // Add new file entry
            enrichedRepo[path] = {
              type: "blob",
              path: typedFile.path,
              fileName: typedFile.fileName,
              content: typedFile.content,
            };
          }
        });
      }

      console.debug(
        `[Store] Starting FlexSearch index build for GitHub archive`
      );

      // Update download progress to indexing phase
      set(
        produce((state: any) => {
          state.downloadProgress = {
            ...state.downloadProgress,
            phase: "processing",
            isDownloading: false,
          };
          state.indexingProgress = {
            total: 0,
            current: 0,
            isIndexing: true,
          };
        })
      );

      let lastProgress = {
        total: 0,
        current: 0,
        isIndexing: true,
      };

      // Build FlexSearch index with pre-loaded content
      const result = await buildFlexSearchIndex(
        enrichedRepo,
        currentRepo,
        (progress) => {
          // Only update if there's an actual change in progress
          if (
            progress.isIndexing &&
            (progress.current !== lastProgress.current ||
              progress.total !== lastProgress.total)
          ) {
            lastProgress = progress;
            console.debug(
              `[Store] Updating indexing progress: ${progress.current}/${
                progress.total
              } (${Math.round((progress.current / progress.total) * 100)}%)`
            );
            set(
              produce((state: any) => {
                state.indexingProgress = progress;
              })
            );
          }
        },
        archiveFiles // Pass pre-loaded content
      );

      console.debug(
        "[Store] GitHub archive index build complete, updating final state"
      );

      // Batch update all indexing-related state at once
      set(
        produce((state: any) => {
          console.debug("[Store] Cleaning up old data");
          // Clean up old data first
          if (state.fileIndex) {
            state.fileIndex = null;
          }
          if (state.documentMap) {
            state.documentMap.clear();
          }

          console.debug("[Store] Setting new data");
          // Set new data
          state.fileIndex = result.fileIndex;
          state.documentMap = result.documentMap;
          state.indexingProgress = result.indexingProgress;

          // Reset download progress
          state.downloadProgress = {
            phase: "idle",
            bytesDownloaded: 0,
            totalBytes: 0,
            filesProcessed: 0,
            totalFiles: 0,
            isDownloading: false,
          };
        })
      );
      console.debug("[Store] Final state update complete for GitHub mode");
    } catch (error) {
      console.error(
        "[Store] Error during GitHub archive download/indexing:",
        error
      );

      // Reset state on error
      set(
        produce((state: any) => {
          state.fileIndex = null;
          if (state.documentMap) {
            state.documentMap.clear();
          }
          state.documentMap = new Map();
          state.indexingProgress = {
            total: 0,
            current: 0,
            isIndexing: false,
          };
          state.downloadProgress = {
            phase: "idle",
            bytesDownloaded: 0,
            totalBytes: 0,
            filesProcessed: 0,
            totalFiles: 0,
            isDownloading: false,
          };
        })
      );

      // Show error notification to user
      const errorMessage = error.message || "Unknown error occurred";

      // Check if it's a detailed error message (contains our emoji indicators)
      if (errorMessage.includes("💡") || errorMessage.includes("🔒")) {
        // Show the detailed error message as-is
        useStore.getState().setErrorNotification(errorMessage);
      } else {
        // Check for specific large repository errors
        if (
          errorMessage.includes("Maximum call stack size exceeded") ||
          errorMessage.includes("out of memory") ||
          errorMessage.includes("Failed to process zip archive")
        ) {
          useStore
            .getState()
            .setErrorNotification(
              `This repository is too large to process in your browser. ` +
                `For large repositories (>10MB or >1000 files), please use Local mode instead. ` +
                `Local mode can handle repositories of any size without browser limitations.`
            );
        } else {
          // Default error message for other cases
          useStore
            .getState()
            .setErrorNotification(
              `Unable to download repository. Please check your internet connection and try again. ` +
                `For large repositories, consider using Local mode instead.`
            );
        }
      }

      throw error;
    } finally {
      // Always reset the indexing flag
      isIndexingInProgress = false;
    }
  } else {
    // Reset FlexSearch related state for non-supported modes
    set(
      produce((state: any) => {
        if (state.fileIndex) {
          state.fileIndex = null;
        }
        if (state.documentMap) {
          state.documentMap.clear();
        }
        state.documentMap = new Map();
        state.indexingProgress = {
          total: 0,
          current: 0,
          isIndexing: false,
        };
        state.downloadProgress = {
          phase: "idle",
          bytesDownloaded: 0,
          totalBytes: 0,
          filesProcessed: 0,
          totalFiles: 0,
          isDownloading: false,
        };
      })
    );
  }
};
